import React, { useState, useEffect, useMemo } from 'react';
import * as LucideIcons from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { useAppContext } from '../AppContext';
import LoadingSpinner from './LoadingSpinner';

const CreatorDashboardTab = () => {
    const { t } = useTranslation();
    const { appFunctions, setMessage, currentUserProfile } = useAppContext();
    const [stats, setStats] = useState({ totalEarnings: 0, subscriberCount: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(true);

    useEffect(() => {
        setStats({
            totalEarnings: currentUserProfile?.totalEarnings || 0,
            subscriberCount: currentUserProfile?.subscriberCount || 0,
        });
        setIsLoading(false);
    }, [currentUserProfile]);

    useEffect(() => {
        const fetchAnalytics = async () => {
            setIsLoadingAnalytics(true);
            const getCreatorAnalytics = httpsCallable(appFunctions, 'getCreatorAnalytics');
            try {
                const result = await getCreatorAnalytics();
                setAnalyticsData(result.data.analytics || []);
            } catch (error) {
                console.error("Error fetching creator analytics:", error);
                setMessage(t('creatorDashboard.loadAnalyticsError'));
            } finally {
                setIsLoadingAnalytics(false);
            }
        };
        fetchAnalytics();
    }, [appFunctions, setMessage, t]);

    const engagementChartData = useMemo(() => {
        if (!analyticsData) return { labels: [], datasets: [] };
        const labels = analyticsData.map(d => new Date(d.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }));
        return {
            labels,
            datasets: [
                {
                    label: t('creatorDashboard.echoesEarned'),
                    data: analyticsData.map(d => d.echoesEarned || 0),
                    borderColor: '#facc15',
                    backgroundColor: 'rgba(250, 204, 21, 0.2)',
                    fill: true,
                    tension: 0.4,
                    yAxisID: 'y',
                    pointBackgroundColor: '#facc15',
                    pointBorderColor: '#fff',
                    pointHoverRadius: 7,
                    pointHoverBorderWidth: 2,
                    pointRadius: 5,
                    shadowColor: 'rgba(250, 204, 21, 0.5)',
                    shadowBlur: 10
                },
                {
                    label: t('creatorDashboard.likesReceived'),
                    data: analyticsData.map(d => d.likes || 0),
                    borderColor: '#f472b6',
                    backgroundColor: 'rgba(244, 114, 182, 0.2)',
                    fill: false,
                    tension: 0.4,
                    yAxisID: 'y1',
                    pointBackgroundColor: '#f472b6',
                    pointBorderColor: '#fff',
                    pointHoverRadius: 7,
                    pointHoverBorderWidth: 2,
                    pointRadius: 5,
                    shadowColor: 'rgba(244, 114, 182, 0.5)',
                    shadowBlur: 10
                }
            ]
        };
    }, [analyticsData, t]);

    const engagementChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        animation: {
            duration: 1000,
            easing: 'easeInOutQuart',
        },
        plugins: {
            title: { display: true, text: t('creatorDashboard.dailyEngagementTitle'), color: '#e5e7eb', font: { size: 16 } },
            legend: { labels: { color: '#d1d5db' } },
            tooltip: {
                enabled: true,
                backgroundColor: 'rgba(17, 24, 39, 0.8)',
                titleColor: '#e5e7eb',
                bodyColor: '#d1d5db',
                borderColor: 'rgba(255, 255, 255, 0.1)',
                borderWidth: 1,
                padding: 10,
                caretSize: 0,
                displayColors: true,
            }
        },
        scales: {
            x: { ticks: { color: '#9ca3af' }, grid: { color: 'rgba(255, 255, 255, 0.1)' } },
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: { display: true, text: t('creatorDashboard.echoesEarned'), color: '#facc15' },
                ticks: { color: '#facc15' },
                grid: { drawOnChartArea: true, color: 'rgba(255, 255, 255, 0.1)' },
            },
            y1: {
                type: 'linear',
                display: true,
                position: 'right',
                title: { display: true, text: t('creatorDashboard.likes'), color: '#f472b6' },
                ticks: { color: '#f472b6' },
                grid: { drawOnChartArea: false },
            },
        },
    };

    if (isLoading) {
        return <LoadingSpinner message={t('creatorDashboard.loadingStats')} />;
    }

    return (
        <div className="animate-fadeIn space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div className="bg-gray-800 p-4 rounded-lg border border-gray-700"><h4 className="text-sm font-bold text-yellow-300">{t('creatorDashboard.totalEchoesEarned')}</h4><p className="text-3xl font-bold text-white flex items-center justify-center mt-1"><LucideIcons.Flame size={24} className="mr-2" />{stats.totalEarnings.toLocaleString()}</p></div>
                <div className="bg-gray-800 p-4 rounded-lg border border-gray-700"><h4 className="text-sm font-bold text-purple-300">{t('creatorDashboard.sealedKeyholders')}</h4><p className="text-3xl font-bold text-white flex items-center justify-center mt-1"><LucideIcons.KeyRound size={24} className="mr-2" />{stats.subscriberCount}</p></div>
                <div className="bg-gray-800 p-4 rounded-lg border border-gray-700"><h4 className="text-sm font-bold text-sky-300">{t('creatorDashboard.reputation')}</h4><p className="text-3xl font-bold text-white flex items-center justify-center mt-1"><LucideIcons.ShieldCheck size={24} className="mr-2" />{currentUserProfile?.reputationScore || 0}</p></div>
            </div>

            {isLoadingAnalytics ? (
                <div className="bg-gray-800/50 p-5 rounded-lg border border-gray-700 h-80 flex items-center justify-center">
                    <LoadingSpinner message={t('creatorDashboard.loadingAnalytics')} />
                </div>
            ) : analyticsData && analyticsData.length > 0 ? (
                <div className="bg-gray-800/50 p-5 rounded-lg border border-gray-700">
                    <div className="h-80">
                        <Line options={engagementChartOptions} data={engagementChartData} />
                    </div>
                </div>
            ) : (
                <div className="bg-gray-800/50 p-5 rounded-lg border border-gray-700 text-center">
                    <LucideIcons.BarChart3 size={48} className="mx-auto mb-4 text-blue-400" />
                    <h4 className="text-lg font-bold text-white">{t('creatorDashboard.noAnalyticsYet')}</h4>
                    <p className="text-gray-400 mt-2">{t('creatorDashboard.noAnalyticsMessage')}</p>
                </div>
            )}
        </div>
    );
};

export default CreatorDashboardTab;
